import { z } from "zod";

export const trelloIdSchema = z
  .string()
  .regex(/^[a-f0-9]{24}$/, "Expected a 24-character Trello ID.");

const trelloDateSchema = z.iso.datetime();
const trelloNullableDateSchema = trelloDateSchema.nullable();

export const trelloBoardSchema = z.object({
  closed: z.boolean(),
  dateLastActivity: trelloDateSchema.optional(),
  desc: z.string(),
  id: trelloIdSchema,
  idOrganization: trelloIdSchema.nullable(),
  name: z.string().min(1),
  prefs: z.object({
    background: z.string().min(1).optional(),
    backgroundBottomColor: z.string().min(1).optional(),
    backgroundBrightness: z.string().min(1).optional(),
    backgroundTile: z.boolean().optional(),
    backgroundTopColor: z.string().min(1).optional(),
    calendarFeedEnabled: z.boolean().optional(),
    cardAging: z.string().min(1).optional(),
    cardCovers: z.boolean(),
    comments: z.string().min(1).optional(),
    hideVotes: z.boolean().optional(),
    isTemplate: z.boolean().optional(),
    permissionLevel: z.string().min(1),
    selfJoin: z.boolean().optional(),
    voting: z.string().min(1).optional(),
  }),
  shortLink: z.string().min(1).optional(),
  shortUrl: z.string().min(1).optional(),
  subscribed: z.boolean().optional(),
  url: z.string().min(1).optional(),
});

export const trelloMemberSchema = z.object({
  fullName: z.string().min(1),
  id: trelloIdSchema,
  username: z.string().min(1),
});

export const trelloListSchema = z.object({
  closed: z.boolean(),
  id: trelloIdSchema,
  idBoard: trelloIdSchema,
  name: z.string().min(1),
  pos: z.number().nonnegative(),
});

export const trelloLabelSchema = z.object({
  color: z.string().min(1).nullable(),
  id: trelloIdSchema,
  idBoard: trelloIdSchema,
  name: z.string(),
});

export const trelloAttachmentSchema = z.object({
  bytes: z.number().int().nonnegative().nullable().optional(),
  date: trelloDateSchema.optional(),
  fileName: z.string().min(1).optional(),
  id: trelloIdSchema,
  isUpload: z.boolean(),
  mimeType: z.string().nullable().optional(),
  name: z.string(),
  url: z.string().min(1).nullable(),
});

const trelloCustomFieldValueSchema = z.union([
  z.object({ checked: z.enum(["true", "false"]) }),
  z.object({ date: trelloDateSchema }),
  z.object({ number: z.string() }),
  z.object({ text: z.string() }),
]);

export const trelloCustomFieldItemSchema = z.object({
  id: trelloIdSchema,
  idCustomField: trelloIdSchema,
  idModel: trelloIdSchema,
  idValue: trelloIdSchema.optional(),
  modelType: z.literal("card"),
  value: trelloCustomFieldValueSchema.optional(),
});

export const trelloCardCoverSchema = z.looseObject({
  brightness: z.string().min(1),
  color: z.string().min(1).nullable(),
  idAttachment: trelloIdSchema.nullable().optional(),
  idUploadedBackground: z
    .union([trelloIdSchema, z.boolean()])
    .nullable()
    .optional(),
  isTemplate: z.boolean().optional(),
  size: z.string().min(1),
});

export const trelloCardSchema = z.object({
  attachments: z.array(trelloAttachmentSchema).default([]),
  closed: z.boolean(),
  cover: trelloCardCoverSchema.optional(),
  customFieldItems: z.array(trelloCustomFieldItemSchema).default([]),
  dateLastActivity: trelloDateSchema.optional(),
  desc: z.string(),
  due: trelloNullableDateSchema,
  dueComplete: z.boolean(),
  dueReminder: z.number().int().nullable().optional(),
  id: trelloIdSchema,
  idAttachmentCover: trelloIdSchema.nullable().optional(),
  idBoard: trelloIdSchema,
  idChecklists: z.array(trelloIdSchema),
  idLabels: z.array(trelloIdSchema),
  idList: trelloIdSchema,
  idMembers: z.array(trelloIdSchema),
  idMembersVoted: z.array(trelloIdSchema).default([]),
  idShort: z.number().int().nonnegative().optional(),
  manualCoverAttachment: z.boolean().optional(),
  name: z.string().min(1),
  pos: z.number().nonnegative(),
  shortLink: z.string().min(1).optional(),
  shortUrl: z.string().min(1).optional(),
  start: trelloNullableDateSchema,
  subscribed: z.boolean().optional(),
  url: z.string().min(1).optional(),
});

const trelloCheckItemSchema = z.object({
  due: trelloNullableDateSchema,
  id: trelloIdSchema,
  idMember: trelloIdSchema.nullable(),
  name: z.string().min(1),
  pos: z.number().nonnegative(),
  state: z.enum(["complete", "incomplete"]),
});

export const trelloChecklistSchema = z.object({
  checkItems: z.array(trelloCheckItemSchema),
  id: trelloIdSchema,
  idBoard: trelloIdSchema,
  idCard: trelloIdSchema,
  name: z.string().min(1),
  pos: z.number().nonnegative(),
});

const trelloActionModelSchema = z.looseObject({
  id: trelloIdSchema,
  name: z.string().optional(),
});

export const trelloActionSchema = z.looseObject({
  data: z.looseObject({
    board: trelloActionModelSchema.optional(),
    card: trelloActionModelSchema.optional(),
    text: z.string().optional(),
  }),
  date: trelloDateSchema,
  display: z.json().optional(),
  id: trelloIdSchema,
  idMemberCreator: trelloIdSchema,
  memberCreator: trelloMemberSchema,
  type: z.string().min(1),
});

export const trelloBoardMembershipSchema = z.looseObject({
  deactivated: z.boolean().optional(),
  id: trelloIdSchema,
  idMember: trelloIdSchema.nullable().optional(),
  memberType: z.string().min(1).nullable().optional(),
  unconfirmed: z.boolean().optional(),
});

const trelloCustomFieldOptionSchema = z.object({
  color: z.string().min(1),
  id: trelloIdSchema,
  idCustomField: trelloIdSchema,
  pos: z.number().nonnegative(),
  value: z.object({ text: z.string() }),
});

export const trelloCustomFieldSchema = z.object({
  display: z
    .object({
      cardFront: z.boolean(),
    })
    .optional(),
  fieldGroup: z.string().min(1),
  id: trelloIdSchema,
  idModel: trelloIdSchema,
  modelType: z.literal("board"),
  name: z.string().min(1),
  options: z.array(trelloCustomFieldOptionSchema).optional(),
  pos: z.number().nonnegative(),
  type: z.enum(["checkbox", "date", "list", "number", "text"]),
});

export const trelloBoardCaptureSchema = z.object({
  actions: z.array(trelloActionSchema),
  board: trelloBoardSchema,
  cards: z.array(trelloCardSchema),
  checklists: z.array(trelloChecklistSchema),
  customFields: z.array(trelloCustomFieldSchema),
  labels: z.array(trelloLabelSchema),
  lists: z.array(trelloListSchema),
  members: z.array(trelloMemberSchema),
  memberships: z.array(trelloBoardMembershipSchema).default([]),
});

export type TrelloBoardCapture = z.infer<typeof trelloBoardCaptureSchema>;
export type TrelloCard = z.infer<typeof trelloCardSchema>;
